/**
 * Where a completed entity switch lands the user (DV-3013 spec §3.5 / D6).
 *
 * `switchEntity` must stay framework-free — `bootstrapAuth` calls it before
 * React renders, so it cannot hold a `useNavigate`. But a post-render switch
 * must now land WITHOUT a document load, which is precisely what react-router
 * owns. This is the one seam between the two, with two honest regimes:
 *
 *   - **A router is mounted** (`useNavigationPort` has wired one): navigate.
 *   - **No router** — the pre-render auth bootstrap: a full-page trip is the
 *     correct and only option, and it is what `bootstrapAuth` already did.
 *
 * The fallback is therefore not a defensive shim; it is the second regime.
 */

export interface LandOptions {
  /** Replace the current history entry instead of pushing one. */
  replace?: boolean;
}

export type LandFn = (
  path: string,
  queryParams?: Record<string, string>,
  options?: LandOptions,
) => void;

let land: LandFn | null = null;

/** Wired once, inside the router tree. Cleared on unmount. */
export const setNavigationPort = (fn: LandFn | null): void => {
  land = fn;
};

/**
 * Path and query are kept apart all the way down so the router never
 * percent-encodes a query string into the pathname — the same reason
 * `consumeIntendedUrl` splits them.
 */
const toUrl = (path: string, queryParams?: Record<string, string>): string => {
  const search = new URLSearchParams(queryParams ?? {}).toString();
  return search ? `${path}?${search}` : path;
};

/**
 * Land the user. Uses the router when one is mounted; a full-page trip when
 * there is none.
 */
export const landOn = (
  path: string,
  queryParams?: Record<string, string>,
  options?: LandOptions,
): void => {
  if (land) {
    land(path, queryParams, options);
    return;
  }
  const url = toUrl(path, queryParams);
  if (options?.replace) window.location.replace(url);
  else window.location.assign(url);
};
